import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { UI } from '../constants/designTokens';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

const ClubChatPage = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [club, setClub] = useState(null);
  const [chatMeta, setChatMeta] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchClub = useCallback(async () => {
    if (!clubId) return;
    try {
      const response = await apiService.clubs.getById(clubId);
      setClub(response.data);
    } catch (e) {
      if (e.response?.status === 404) navigate('/messages');
    }
  }, [clubId, navigate]);

  const fetchChatMeta = useCallback(async () => {
    if (!clubId) return;
    try {
      const response = await apiService.clubs.getClubChat(clubId);
      setChatMeta(response.data);
    } catch (e) {
      if (e.response?.status === 403 || e.response?.status === 404) navigate('/messages');
    }
  }, [clubId, navigate]);

  const fetchMessages = useCallback(async (before = null) => {
    if (!clubId) return;
    try {
      const params = before ? { before } : {};
      const response = await apiService.clubs.getClubChatMessages(clubId, params);
      const newMessages = response.data || [];
      if (before) {
        setMessages(prev => [...newMessages, ...prev]);
      } else {
        setMessages(newMessages);
        setTimeout(scrollToBottom, 100);
      }
    } catch (e) {
      console.error('Error fetching club chat messages:', e);
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  const markAsRead = useCallback(async () => {
    if (!clubId) return;
    try {
      await apiService.clubs.markClubChatRead(clubId);
    } catch (e) {
      console.error('Error marking club chat read:', e);
    }
  }, [clubId]);

  useEffect(() => {
    if (clubId && user) {
      setLoading(true);
      fetchClub();
      fetchChatMeta();
      fetchMessages();
      markAsRead();
    }
  }, [clubId, user, fetchClub, fetchChatMeta, fetchMessages, markAsRead]);

  useEffect(() => {
    if (messages.length > 0 && !loading) scrollToBottom();
  }, [messages.length, loading]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || sending) return;
    const content = messageInput.trim();
    setMessageInput('');
    setSending(true);
    try {
      const response = await apiService.clubs.sendClubChatMessage(clubId, content);
      setMessages(prev => [...prev, response.data]);
      setChatMeta(prev => prev ? { ...prev, last_message_at: response.data.created_at, last_message_preview: content.slice(0, 100) } : null);
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessageInput(content);
    } finally {
      setSending(false);
    }
  };

  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const isToday = format(date, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
    return isToday ? format(date, 'h:mm a') : format(date, 'MMM d, h:mm a');
  };

  const formatDateHeader = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const isToday = format(date, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
    return isToday ? 'Today' : format(date, 'MMMM d, yyyy');
  };

  const groupedMessages = messages.reduce((groups, message) => {
    const date = format(new Date(message.created_at), 'yyyy-MM-dd');
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: UI.COLORS.BACKGROUND }}>
        <div style={{ color: UI.COLORS.TEXT_PRIMARY }}>Please log in to view club chat</div>
      </div>
    );
  }

  if (loading && !chatMeta && messages.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: UI.COLORS.BACKGROUND }}>
        <div style={{ color: UI.COLORS.TEXT_SECONDARY }}>Loading club chat...</div>
      </div>
    );
  }

  const clubName = club?.name || 'Club';

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: UI.COLORS.BACKGROUND, color: UI.COLORS.TEXT_PRIMARY }}>
      <div className="sticky top-0 z-10" style={{ backgroundColor: UI.COLORS.BACKGROUND, borderBottom: `1px solid ${UI.COLORS.BORDER}` }}>
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <button
              onClick={() => navigate('/messages')}
              className="p-2 hover:opacity-70 transition-opacity flex-shrink-0"
              style={{ color: UI.COLORS.TEXT_PRIMARY }}
            >
              <ArrowLeft size={24} />
            </button>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: club?.profile_picture ? 'transparent' : UI.COLORS.ACCENT }}
              >
                {club?.profile_picture ? (
                  <img src={club.profile_picture} alt={clubName} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <span style={{ color: UI.COLORS.WHITE, fontFamily: UI.FONT_FAMILY.BOLD, fontSize: UI.FONT_SIZES.SM }}>
                    {clubName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold truncate" style={{ fontFamily: UI.FONT_FAMILY.BOLD, fontSize: UI.FONT_SIZES.LG }}>
                  {clubName}
                </h2>
                <p className="text-sm truncate" style={{ color: UI.COLORS.TEXT_TERTIARY }}>Group chat</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div style={{ color: UI.COLORS.TEXT_SECONDARY }}>Loading messages...</div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="text-center" style={{ color: UI.COLORS.TEXT_SECONDARY }}>
              No messages yet. Say hello to the group!
            </div>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date}>
              <div className="text-center my-4" style={{ color: UI.COLORS.TEXT_TERTIARY, fontSize: UI.FONT_SIZES.SM }}>
                {formatDateHeader(dateMessages[0].created_at)}
              </div>
              {dateMessages.map((message) => {
                const isOwnMessage = message.sender_id === user.id;
                return (
                  <div key={message.id} className="mb-4">
                    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[75%] rounded-lg px-4 py-2 ${isOwnMessage ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
                        style={{
                          backgroundColor: isOwnMessage ? UI.COLORS.ACCENT : UI.COLORS.FOREGROUND,
                          color: isOwnMessage ? UI.COLORS.WHITE : UI.COLORS.TEXT_PRIMARY,
                          border: isOwnMessage ? 'none' : `1px solid ${UI.COLORS.BORDER}`,
                        }}
                      >
                        {!isOwnMessage && (
                          <p className="text-xs font-medium mb-1" style={{ color: UI.COLORS.TEXT_TERTIARY }}>
                            {message.sender_name}
                          </p>
                        )}
                        <p style={{ fontFamily: UI.FONT_FAMILY.PRIMARY, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {message.content}
                        </p>
                        <p className="text-xs mt-1" style={{ opacity: 0.8 }}>
                          {formatMessageTime(message.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSendMessage} className="p-4" style={{ borderTop: `1px solid ${UI.COLORS.BORDER}`, backgroundColor: UI.COLORS.BACKGROUND }}>
        <div className="flex gap-2">
          <input
            type="text"
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            placeholder="Message the group..."
            className="flex-1 px-4 py-2 rounded-lg"
            style={{
              backgroundColor: UI.COLORS.FOREGROUND,
              color: UI.COLORS.TEXT_PRIMARY,
              border: `1px solid ${UI.COLORS.BORDER}`,
              fontFamily: UI.FONT_FAMILY.PRIMARY,
            }}
          />
          <button
            type="submit"
            disabled={sending || !messageInput.trim()}
            className="p-2 rounded-lg flex-shrink-0 disabled:opacity-50"
            style={{ backgroundColor: UI.COLORS.ACCENT, color: UI.COLORS.WHITE }}
          >
            <Send size={22} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClubChatPage;
